import classNames from 'clsx';
import React, { type FC, type MouseEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { datalistInlineRenameEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { getSearchContentStatus } from 'reactApp/modules/search/search.selectors';
import { STORAGE_CONFIG } from 'reactApp/modules/storage/storage.config';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ContentEditable } from 'reactApp/ui/ContentEditable/ContentEditable';
import { NoAccessItem } from 'reactApp/ui/DataListItemRow/components/NoAccessItem/NoAccessItem';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { ESearchOptionSource } from 'reactApp/ui/WebSearch/WebSearch.data';
import { WebSearchSource } from 'reactApp/ui/WebSearch/WebSearchSource/WebSearchSource';
import { formatAutoDeleteExpires } from 'reactApp/utils/formatDate';
import { noop } from 'reactApp/utils/helpers';

import styles from '../DataListItemThumb.css';
import type { DataListItemThumbProps } from '../DataListItemThumb.types';
import { Favorites } from './Favorites';

type DataListItemThumbMetaProps = DataListItemThumbProps & {
    isEdit: boolean;
    isRenameAvailable?: boolean;
    isAllDocumentsAccessDenied?: boolean;
    handleDoubleClick: (e: MouseEvent) => void;
    onRename: (value: { value: string; newExt: string }) => Promise<any>;
};

export const DataListItemThumbMeta: FC<DataListItemThumbMetaProps> = ({
    isEdit,
    id,
    isRenameAvailable,
    isAllDocumentsAccessDenied,
    storage,
    denyDoAction,
    isPopup,
    deniedByRights,
    name,
    ext,
    handleDoubleClick,
    onRename,
    ...props
}) => {
    const { webSearchContentEnabled } = useSelector(getSearchContentStatus);
    const config = STORAGE_CONFIG[storage];

    const renderSearchSource = useMemo(() => {
        const isContentMatch = 'srchSrc' in props && props.srchSrc === ESearchOptionSource.content;

        if (!isContentMatch || !webSearchContentEnabled || storage !== EStorageType.search || !props.query) {
            return null;
        }

        return <WebSearchSource query={props.query} />;
    }, [props, webSearchContentEnabled, storage]);

    const renderPublicExpires = useMemo(() => {
        if (renderSearchSource || denyDoAction || isPopup) {
            return null;
        }

        const expires = 'publicExpires' in props && props.publicExpires;
        const isAutoDelete = 'isAutoDelete' in props && props.isAutoDelete;

        if (!!expires && isAutoDelete) {
            return <div className={styles.expires}>Удалится {formatAutoDeleteExpires(expires, true)}</div>;
        }
    }, [renderSearchSource, denyDoAction, isPopup, props]);

    const renderFavorites = () => {
        const isFavorites = storage === EStorageType.favorites;
        const isBannedForFavorite = 'listOfBannedToFavoriteItems' in props && props.listOfBannedToFavoriteItems.includes(id);
        const isFavoritesEnable = config.favorites && 'favoritesAvailable' in props && props.favoritesAvailable && !isBannedForFavorite;

        if (denyDoAction || deniedByRights || !isFavoritesEnable || isPopup) {
            return null;
        }

        return (
            <Favorites
                isActive={'isInFavorites' in props && Boolean(props.isInFavorites)}
                onClick={'onFavorites' in props ? props.onFavorites : noop}
                isActiveHidden={isFavorites}
            />
        );
    };

    if (isAllDocumentsAccessDenied) {
        return <NoAccessItem />;
    }

    return (
        <div
            className={classNames(styles.meta, {
                [styles.metaEdit]: isEdit,
            })}
        >
            <div
                className={classNames(styles.name, {
                    [styles.nameEdit]: isEdit,
                })}
            >
                {!isEdit && renderFavorites()}
                {isEdit ? (
                    <div className={styles.edit}>
                        <ContentEditable value={name} onEdit={onRename} selectAllOnFocus extension={ext} />
                    </div>
                ) : (
                    <div className={styles.nameWrapper}>
                        <NameComponent
                            name={name}
                            extension={ext}
                            showTooltip
                            onClick={isRenameAvailable && datalistInlineRenameEnabled ? handleDoubleClick : noop}
                            saveFormatting
                        />
                    </div>
                )}
            </div>
            {renderSearchSource}
            {renderPublicExpires}
        </div>
    );
};
